import { cn } from '../../lib/cn';

/**
 * Shared class recipes for the ONE underline tab language.
 * Used by ui/Tabs (Radix, button-based) and components/SubTabBar (router
 * <Link>-based second-level nav) so both render identically.
 * Underline = "changes what you are looking at". There is no pill/boxed
 * variant; filtering belongs to ToolbarChip.
 */

export interface TabItemStyleOpts {
  active: boolean;
  /** Attention accent: warn-colored label + indicator for alert-bearing tabs. */
  amber?: boolean;
  /** Aligns the first label's text with the 24px content gutter below it
   *  (item px = page gutter, nav px = 0). */
  inset?: boolean;
  /** primary = case-level nav (36px items). secondary = in-page (32px items). */
  level?: 'primary' | 'secondary';
}

/** Tab-item recipe: t-label, 2px underline, no layout shift on select. */
export function tabItemClass({
  active,
  amber = false,
  inset = false,
  level = 'primary',
}: TabItemStyleOpts): string {
  return cn(
    't-label inline-flex items-center gap-2 whitespace-nowrap border-b-2 transition-colors',
    level === 'primary' ? 'h-[var(--size-control-lg)]' : 'h-[var(--size-control-md)]',
    inset ? 'px-6' : 'px-4',
    active
      ? amber
        ? 'border-warn text-ink'
        : 'border-green-700 text-ink'
      : amber
        ? 'border-transparent text-warn'
        : 'border-transparent text-ink-faint hover:text-ink-soft',
    'disabled:cursor-not-allowed disabled:text-ink-disabled',
  );
}

/** Count-badge recipe for tab items (radius-pill is legal on count badges). */
export function tabCountClass({ active, amber = false }: { active: boolean; amber?: boolean }): string {
  return cn(
    'inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-2xs font-semibold leading-none',
    amber
      ? 'bg-warn-soft text-warn'
      : active
        ? 'bg-green-100 text-green-900'
        : 'bg-cream-dark text-ink-soft',
  );
}
